import * as React from 'react';
import * as moment from 'moment';

import Menu from '../components/Menu';
import MenuItem from '../components/MenuItem';
import { VideoData } from 'data/VideoData';
import { useUser } from 'hooks/useUser';
import { deleteVideoNode, getVideos } from 'utils/FirebaseUtils';
import { Popover } from 'react-tiny-popover';
import { ButtonIcon } from 'controller/components/ButtonIcon';
import { generateClassName } from 'utils';
import { reportBFError } from '../utils/Tracker';
import { deleteFileWithUrl } from 'utils/S3Utils';
import { VideoPlayer } from './VideoPlayer';
import { Modal } from 'components/Modal';
import { download } from 'utils/Download';
import { Spinner } from 'components/Spinner';
import { EmptyState } from './EmptyState';

const formatSize = (kilobytes: number): string => {
  let mbs = kilobytes / 1000;
  return Math.round(mbs * 100) / 100 + 'MB';
};

const getVideoBaseUrl = (video: VideoData): string => {
  return `https://s3-${video.region}.amazonaws.com/${video.bucket}/${video.name}`;
};

const getVideoThumbnailUrl = (video: VideoData): string => {
  return `${getVideoBaseUrl(video)}.jpg`;
};

const getVideoUrl = (video: VideoData): string => {
  return `${getVideoBaseUrl(video)}.mp4`;
};

interface ItemProps {
  data: VideoData;
  onSelect: (videoUrl: string) => void;
  onDelete: (video: VideoData) => void;
}

const ItemView: React.FC<ItemProps> = ({ data, onSelect, onDelete }) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const thumbUrl = getVideoThumbnailUrl(data);
  const videoUrl = getVideoUrl(data);

  const didClick = React.useCallback(() => {
    onSelect(videoUrl);
  }, [onSelect, videoUrl]);

  const onMenuClick = React.useCallback(
    (key: string, e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      switch (key) {
        case 'delete':
          onDelete(data);
          break;
        default:
          break;
      }
      setIsOpen(false);
    },
    [onDelete, data]
  );

  const menu = (
    <Menu onClick={onMenuClick}>
      <MenuItem actionKey="delete" className="red">
        Delete
      </MenuItem>
    </Menu>
  );

  return (
    <div className="flyer-item" onClick={didClick}>
      <img
        src={thumbUrl}
        // crossOrigin="anonymous"
      />
      <div className="flyer-item-text">
        <div className="title">
          {formatSize(data.size)}
          <span className="date"> - {moment(data.date!).fromNow()}</span>
        </div>
        <div className="meta">
          {`Duration: ${Math.round(data.duration / 1000)} s \u00A0 Layers: ${
            data.layers
          }`}
        </div>
      </div>
      <ButtonIcon
        icon="download"
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
          download(videoUrl);
        }}
      />
      <div style={{ width: 8 }} />
      <Popover
        isOpen={isOpen}
        positions={['bottom', 'top']}
        align="end"
        reposition={true}
        content={menu}
        onClickOutside={() => {
          setIsOpen(false);
        }}
      >
        <ButtonIcon
          icon="options"
          active={isOpen}
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            setIsOpen(true);
          }}
        />
      </Popover>
    </div>
  );
};

enum ViewState {
  Loading = 'Loading...',
  Ready = 'Ready',
  Renaming = 'Renaming...',
  Deleting = 'Deleting...',
  Copying = 'Copying...'
}

export const Videos: React.FC = () => {
  const mounted = React.useRef(false);
  const [videos, setVideos] = React.useState<VideoData[]>([]);
  const [viewState, setViewState] = React.useState<ViewState>(
    ViewState.Loading
  );
  const [previewVideoUrl, setPreviewVideoUrl] = React.useState<
    string | undefined
  >();

  const user = useUser();

  const loadVideos = React.useCallback(() => {
    if (user) {
      getVideos(user.uid).then((items: VideoData[]) => {
        if (mounted.current) {
          setVideos(items);
          setViewState(ViewState.Ready);
        }
      });
    }
  }, [user]);

  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  React.useEffect(() => {
    if (user) {
      loadVideos();
    }
  }, [user]);

  const selectVideo = React.useCallback((videoUrl: string) => {
    setPreviewVideoUrl(videoUrl);
  }, []);

  const deleteVideo = React.useCallback(
    async (video: VideoData) => {
      try {
        if (viewState !== ViewState.Ready) {
          return;
        }
        setViewState(ViewState.Deleting);
        await deleteVideoNode(video.id);
        await deleteFileWithUrl(getVideoThumbnailUrl(video));
        await deleteFileWithUrl(getVideoUrl(video));
        const index = videos.indexOf(video);
        const newVideos = [...videos];
        newVideos.splice(index, 1);
        setVideos(newVideos);
        setViewState(ViewState.Ready);
      } catch (error) {
        alert(`Could not delete the flyer because of ${error.message}`);
        reportBFError(
          new Error('Could not delete the flyer because of ' + error.message)
        );
        setViewState(ViewState.Ready);
      }
    },
    [viewState]
  );

  if (viewState === ViewState.Loading) {
    return <Spinner />;
  }

  if (videos.length === 0) {
    return <EmptyState>You haven’t any video in the cloud yet.</EmptyState>;
  }

  return (
    <div
      className={generateClassName('flyer-list', {
        deleting: viewState === ViewState.Deleting
      })}
    >
      {videos.map(video => {
        return (
          <ItemView
            data={video}
            key={video.id}
            onSelect={selectVideo}
            onDelete={deleteVideo}
          />
        );
      })}
      {previewVideoUrl && (
        <Modal>
          <VideoPlayer
            url={previewVideoUrl}
            onClose={() => setPreviewVideoUrl(undefined)}
          />
        </Modal>
      )}
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/library/Videos.tsx